import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type StatusType = 'pending' | 'approved' | 'rejected' | string;

interface StatusBadgeProps {
  status: StatusType;
  /** Whether the transaction was invalidated by a policy change */
  invalidated?: boolean;
  variant?: 'default' | 'outline';
  className?: string;
  currentApprovals?: number;
  requiredApprovals?: number;
}

export function StatusBadge({
  status,
  invalidated = false,
  variant = 'outline',
  className,
  currentApprovals,
  requiredApprovals,
}: StatusBadgeProps) {
  // Invalidated takes precedence over other statuses
  const effectiveStatus = invalidated ? 'invalidated' : status;

  const getStatusStyles = (statusToStyle: StatusType) => {
    switch (statusToStyle) {
      case 'completed':
        return variant === 'default'
          ? 'bg-green-500'
          : 'bg-green-20 text-green-800 dark:bg-green-800/20 dark:text-green-400 border-green-400';
      case 'processing':
        return variant === 'default'
          ? 'bg-blue-500'
          : 'bg-blue-20 text-blue-800 dark:bg-blue-800/20 dark:text-blue-400 border-blue-400';
      case 'approvalReady':
      case 'approvalAndRejectionReady':
        return variant === 'default'
          ? 'bg-blue-500'
          : 'bg-blue-20 text-blue-800 dark:bg-blue-800/20 dark:text-blue-400 border-blue-400';
      case 'approved':
        return variant === 'default'
          ? 'bg-green-500'
          : 'bg-green-20 text-green-800 dark:bg-green-800/20 dark:text-green-400 border-green-400';
      case 'pending':
      case 'pendingReviewSignatures':
      case 'pendingInitiatorSignature':
        return variant === 'default'
          ? 'bg-yellow-500'
          : 'bg-yellow-20 text-yellow-800 dark:bg-yellow-800/20 dark:text-yellow-400 border-yellow-400';
      case 'rejected':
      case 'rejectionReady':
        return variant === 'default'
          ? 'bg-red-500'
          : 'bg-red-20 text-red-800 dark:bg-red-800/20 dark:text-red-400 border-red-400';
      case 'failed':
        return variant === 'default'
          ? 'bg-amber-500'
          : 'bg-amber-50 text-amber-700 dark:bg-amber-800/20 dark:text-amber-400 border-amber-400';
      case 'blocked':
      case 'expired':
        return variant === 'default'
          ? 'bg-gray-500'
          : 'bg-gray-100 text-gray-700 dark:bg-gray-800/20 dark:text-gray-300 border-gray-400';
      case 'invalidated':
        return variant === 'default'
          ? 'bg-orange-500'
          : 'bg-orange-50 text-orange-700 dark:bg-orange-800/20 dark:text-orange-400 border-orange-400';
      default:
        return '';
    }
  };

  const getStatusLabel = (statusToLabel: StatusType) => {
    // Map status to display labels
    switch (statusToLabel) {
      case 'invalidated':
        return 'Invalidated';
      case 'pending':
        return 'Awaiting approval';
      case 'pendingInitiatorSignature':
        return 'Awaiting initiator';
      case 'pendingReviewSignatures':
        return 'Awaiting review';
      case 'approvalReady':
        return 'Ready to execute';
      case 'rejectionReady':
        return 'Ready to reject';
      case 'approvalAndRejectionReady':
        return 'Ready to execute/reject';
      case 'processing':
        return 'Execution in progress';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Reverted';
      case 'blocked':
        return 'Blocked';
      case 'expired':
        return 'Expired';
      case 'approved':
        return 'Approved';
      default:
        // Convert from camelCase or snake_case to Title Case
        return statusToLabel
          .replace(/([A-Z])/g, ' $1') // insert space before capital letters
          .replace(/_/g, ' ') // replace underscores with spaces
          .replace(/^\w/, (c) => c.toUpperCase()) // capitalize first letter
          .trim();
    }
  };

  const renderStatusContent = () => {
    const label = getStatusLabel(effectiveStatus);

    // Don't show approval counts for invalidated transactions
    if (
      (effectiveStatus === 'pending' ||
        effectiveStatus === 'pendingReviewSignatures') &&
      currentApprovals !== undefined &&
      requiredApprovals !== undefined
    ) {
      return (
        <>
          <span className="font-semibold">
            {label} ({currentApprovals}/{requiredApprovals})
          </span>
        </>
      );
    }

    return label;
  };

  return (
    <Badge
      variant={variant}
      className={cn(getStatusStyles(effectiveStatus), className)}
    >
      {renderStatusContent()}
    </Badge>
  );
}
